import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useLocale } from "@/i18n";

// Backend usa weekday Python (seg=0..dom=6); JS getDay() tem dom=0..sab=6.
const jsToPyWeekday = (jsDay) => (jsDay + 6) % 7;

function buildDays(maxDays, schedule) {
  const days = [];
  const today = new Date();
  for (let i = 0; i <= maxDays; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    if (!schedule?.[String(jsToPyWeekday(d.getDay()))]) continue;
    days.push(d);
  }
  return days;
}

const toISO = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export default function DateTimeStep({ apiBase, service, schedule, maxDaysAhead, date, time, onDateChange, onTimeChange, professionalId }) {
  const { t, intlLocale } = useLocale();
  const weekdayFmt = useMemo(() => new Intl.DateTimeFormat(intlLocale, { weekday: "short" }), [intlLocale]);
  const dayFmt = useMemo(() => new Intl.DateTimeFormat(intlLocale, { day: "2-digit", month: "short" }), [intlLocale]);
  const days = useMemo(() => buildDays(maxDaysAhead, schedule), [maxDaysAhead, schedule]);
  const [slots, setSlots] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!date && days.length) onDateChange(toISO(days[0]));
  }, [date, days, onDateChange]);

  useEffect(() => {
    if (!date || !service) return;
    setLoading(true);
    setSlots(null);
    axios
      .get(`${apiBase}/slots`, { params: { date, service_id: service.id, ...(professionalId ? { professional_id: professionalId } : {}) } })
      .then((res) => setSlots(res.data.slots))
      .catch(() => toast.error(t("slots_error")))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase, date, service, professionalId]);

  return (
    <section data-testid="datetime-step">
      <p className="text-xs uppercase tracking-[0.2em] font-semibold text-amber-500">{t("step2")}</p>
      <h2 className="text-2xl font-bold tracking-tight text-zinc-100 mt-1">{t("choose_datetime")}</h2>
      <p className="text-sm text-zinc-500 mt-1">
        {service.name} · {service.duration_min} min
      </p>

      <div className="flex gap-2 overflow-x-auto no-scrollbar mt-5 -mx-4 px-4 pb-1" data-testid="date-strip">
        {days.map((d) => {
          const iso = toISO(d);
          const active = date === iso;
          return (
            <button
              key={iso}
              data-testid={`date-chip-${iso}`}
              onClick={() => onDateChange(iso)}
              className={`shrink-0 flex flex-col items-center justify-center min-w-[64px] h-[72px] rounded-xl border transition-colors duration-200 ${
                active
                  ? "bg-amber-500 text-zinc-950 border-amber-500"
                  : "bg-zinc-900 border-zinc-800 text-zinc-300 hover:border-zinc-700"
              }`}
            >
              <span className={`text-[11px] uppercase font-semibold ${active ? "text-zinc-800" : "text-zinc-500"}`}>
                {weekdayFmt.format(d).replace(".", "")}
              </span>
              <span className="text-sm font-bold mt-0.5">{dayFmt.format(d).replace(".", "")}</span>
            </button>
          );
        })}
      </div>

      <div className="mt-6" data-testid="time-slots">
        {loading && (
          <div className="flex justify-center py-8" data-testid="slots-loading">
            <div className="h-8 w-8 rounded-full border-2 border-amber-500 border-t-transparent animate-spin" />
          </div>
        )}

        {!loading && slots && (
          <>
            {slots.every((s) => !s.available) ? (
              <p className="text-sm text-zinc-400 bg-zinc-900 border border-zinc-800 rounded-xl p-4" data-testid="no-slots-msg">
                {t("no_slots")}
              </p>
            ) : (
              <div className="grid grid-cols-4 gap-3">
                {slots.map((s, i) => {
                  const active = time === s.time;
                  return (
                    <button
                      key={s.time}
                      data-testid={`time-slot-${s.time}`}
                      disabled={!s.available}
                      onClick={() => onTimeChange(s.time)}
                      style={{ animationDelay: `${i * 20}ms` }}
                      className={`animate-fade-up min-h-[44px] rounded-lg border text-sm font-semibold transition-colors duration-200 active:scale-95 ${
                        active
                          ? "bg-amber-500 text-zinc-950 border-amber-500 font-bold"
                          : s.available
                            ? "border-zinc-800 text-zinc-300 bg-zinc-900 hover:border-amber-500/60"
                            : "border-zinc-800/50 text-zinc-600 opacity-50 line-through cursor-not-allowed"
                      }`}
                    >
                      {s.time}
                    </button>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}
